import { Injectable, signal, WritableSignal } from '@angular/core';
import { catchError, map, Observable, of } from 'rxjs';
import { GlobalErrorService } from '@services/global-error.service';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '@environments/environment';

export interface VendorOpenPositionsQuery {
  status?: string;
  lob?: string;
  leader?: string;
  location?: string;
  jobTitle?: string;
  openedDate?: string;
  desiredStartDate?: string;
  search?: string;
  page?: number;
  limit?: number;
  sort?: string;
  dir?: string;
}

export interface VendorOpenPositionsPayload {
  data: any[];
  totalRecords: number;
  currentPage: number;
  limit: number;
  totalPages: number;
  facets: { [key: string]: string[] };
}

@Injectable({
  providedIn: 'root',
})
export class OpenPositionsService {
  private baseUrl = environment.apiUrl;
  // Holds the latest paged payload (data + paging + facets).
  openPositionsSignal: WritableSignal<VendorOpenPositionsPayload | null> = signal(null);

  constructor(
    private http: HttpClient,
    private errorHandler: GlobalErrorService,
  ) {}

  /**
   * Fetch one page of the caller's own open positions. Vendor scope is
   * derived server-side from the JWT — the client no longer sends vendorId.
   * Status, filters, and pagination are all applied on the server.
   */
  getVendorPositions(query: VendorOpenPositionsQuery = {}): Observable<VendorOpenPositionsPayload | null> {
    let params = new HttpParams()
      .set('status', query.status || 'Open')
      .set('page', String(query.page ?? 1))
      .set('limit', String(query.limit ?? 20));
    if (query.lob) params = params.set('lob', query.lob);
    if (query.leader) params = params.set('leader', query.leader);
    if (query.location) params = params.set('location', query.location);
    if (query.jobTitle) params = params.set('jobTitle', query.jobTitle);
    if (query.openedDate) params = params.set('openedDate', query.openedDate);
    if (query.desiredStartDate) params = params.set('desiredStartDate', query.desiredStartDate);
    if (query.search) params = params.set('search', query.search);
    if (query.sort) params = params.set('sort', query.sort);
    if (query.dir) params = params.set('dir', query.dir);

    return this.http
      .get<{ data: VendorOpenPositionsPayload }>(`${this.baseUrl}/vendor/open-positions/paged`, { params })
      .pipe(
        map((response) => response?.data ?? null),
        catchError((error) => {
          this.errorHandler.logError(error);
          return of(null);
        }),
      );
  }
}
